import React, { useEffect, useState } from 'react';
import '../../assets/fontello-change/fontello-79be5077/css/fontello.css';
import { getSiteContent } from '../../utils/siteContent';

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend-production-958f6.up.railway.app';

const TicketModal = ({ event, onClose }) => {
  const [siteContent, setSiteContent] = useState(() => getSiteContent());
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [bookingForm, setBookingForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    attendees: '1',
    preferredDate: '',
    notes: '',
  });

  useEffect(() => {
    const handleContentUpdate = () => setSiteContent(getSiteContent());
    window.addEventListener('site-content-updated', handleContentUpdate);
    return () => window.removeEventListener('site-content-updated', handleContentUpdate);
  }, []);

  const handleCheckout = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${BACKEND_BASE_URL}/bookings/event-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_title: event?.title || 'Session booking',
          group_name: event?.title || 'Session booking',
          full_name: bookingForm.fullName,
          email: bookingForm.email,
          phone: bookingForm.phone,
          attendees: Number(bookingForm.attendees || 1),
          preferred_date: bookingForm.preferredDate,
          notes: bookingForm.notes,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Unable to send your booking request right now.');
      }

      window.dispatchEvent(new Event('booking-request-updated'));

      setStep(2);
      setBookingForm({
        fullName: '',
        email: '',
        phone: '',
        attendees: '1',
        preferredDate: '',
        notes: '',
      });
    } catch (error) {
      setErrorMessage(error.message || 'Unable to send your booking request right now.');
    } finally {
      setLoading(false);
    }
  };

  if (!event) return null;

  return (
    <div className="ticket-modal-overlay" onClick={onClose}>
      <div className="ticket-modal-card" onClick={e => e.stopPropagation()} style={{ width: 'min(900px, calc(100vw - 24px))', maxWidth: '900px' }}>
        <button className="ticket-modal-close" onClick={onClose} aria-label="Close booking dialog">
          <i className="icon-left-circled" />
        </button>

        {step === 1 ? (
          <form onSubmit={handleCheckout}>
            <h2 className="ticket-modal-title">{siteContent.forms.trustBookingFormTitle}</h2>
            <p className="ticket-modal-subtitle">
              {siteContent.forms.trustBookingFormSubtitlePrefix} {event.title}
            </p>

            <div className="ticket-selection">
              <div className="ticket-type">
                <div>
                  <h4 style={{ margin: 0 }}>Session Booking</h4>
                  <span style={{ fontSize: '0.9rem', color: '#666' }}>Request your place for this session</span>
                </div>
                <div style={{ fontWeight: 'bold' }}>Booking Request</div>
              </div>

              <div style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span style={{ fontWeight: 600 }}>Full name *</span>
                  <input required value={bookingForm.fullName} onChange={e => setBookingForm({ ...bookingForm, fullName: e.target.value })} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }} />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span style={{ fontWeight: 600 }}>Email *</span>
                  <input required type="email" value={bookingForm.email} onChange={e => setBookingForm({ ...bookingForm, email: e.target.value })} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }} />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span style={{ fontWeight: 600 }}>Phone</span>
                  <input value={bookingForm.phone} onChange={e => setBookingForm({ ...bookingForm, phone: e.target.value })} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }} />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span style={{ fontWeight: 600 }}>Attendees *</span>
                  <input required type="number" min="1" max="10" value={bookingForm.attendees} onChange={e => setBookingForm({ ...bookingForm, attendees: e.target.value })} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }} />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span style={{ fontWeight: 600 }}>Preferred date</span>
                  <input type="date" value={bookingForm.preferredDate} onChange={e => setBookingForm({ ...bookingForm, preferredDate: e.target.value })} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }} />
                </label>
                <label style={{ display: 'grid', gap: '6px' }}>
                  <span style={{ fontWeight: 600 }}>Notes</span>
                  <textarea rows="4" value={bookingForm.notes} onChange={e => setBookingForm({ ...bookingForm, notes: e.target.value })} style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', minHeight: '90px', resize: 'vertical' }} placeholder="Tell us what you would like to attend or any accessibility needs." />
                </label>
              </div>
            </div>

            {errorMessage && (
              <div style={{ marginTop: '12px', padding: '10px', borderRadius: '8px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
                {errorMessage}
              </div>
            )}

            <button className="ticket-modal-submit" type="submit" disabled={loading} style={{ marginTop: '16px' }}>
              {loading ? 'Sending...' : `Submit booking for ${bookingForm.attendees || '1'} attendee${Number(bookingForm.attendees || 1) > 1 ? 's' : ''}`}
            </button>
          </form>
        ) : (
          <div className="ticket-modal-success">
            <div className="success-icon"><i className="icon-book-open" /></div>
            <h2>Booking Request Sent!</h2>
            <p>Your booking request for {bookingForm.attendees || '1'} attendee{Number(bookingForm.attendees || 1) > 1 ? 's' : ''} for {event.title} has been submitted.</p>
            <p style={{ fontSize: '0.9rem', color: '#666' }}>The trust team will review your request and contact you shortly.</p>
            <button className="ticket-modal-submit" onClick={onClose} style={{ marginTop: '20px' }}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketModal;
